/* S3 Professional Capture — s3-professional-capture.js
   Controller for sections/s3-professional-capture.liquid: modal open/close with
   focus trap, the sticky-CTA observer, validation, and the Klaviyo client-side
   submit (/client/subscriptions + /client/events, revision 2026-04-15).
   Loaded with defer, and self-guarding so it is safe to load more than once. */

(function () {
  if (window.customElements && customElements.get('s3-pro-capture')) return;

  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  function labelFor(key) {
    return key.replace(/^utm_/, 'UTM ').replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '');
  }

  function S3ProCapture() { return Reflect.construct(HTMLElement, [], S3ProCapture); }
  S3ProCapture.prototype = Object.create(HTMLElement.prototype);
  Object.setPrototypeOf(S3ProCapture, HTMLElement);
  var proto = S3ProCapture.prototype;

  proto.connectedCallback = function () {
    var self = this;

    this.companyId = this.dataset.companyId || '';
    this.listId = this.dataset.listId || '';
    this.customSource = this.dataset.customSource || 'Professionals page';
    this.extraParams = (this.dataset.extraParams || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);

    this.modal = this.querySelector('[data-capture-modal]');
    this.form = this.querySelector('[data-capture-form]');
    this.formState = this.querySelector('[data-capture-formstate]');
    this.successEl = this.querySelector('[data-capture-success]');
    this.errorEl = this.querySelector('[data-capture-error]');
    this.submitBtn = this.querySelector('[data-capture-submit]');
    this.sticky = this.querySelector('[data-capture-sticky]');
    this._captureFired = false;

    this.querySelectorAll('[data-capture-close]').forEach(function (el) {
      el.addEventListener('click', function () { self.closeModal('close'); });
    });

    this._onKey = function (e) {
      if (e.key === 'Escape') self.closeModal('escape');
      if (e.key === 'Tab') self.trapTab(e);
    };

    if (this.form) this.form.addEventListener('submit', function (e) { self.onSubmit(e); });

    this.initSticky();
  };

  /* ── Sticky bar ──────────────────────────────────────────────────────
     Shown once the hero CTA has scrolled out of view above the fold, so it
     never competes with the CTA already on screen. Observes the first
     in-page trigger; falls back to a scroll threshold if there isn't one. */
  proto.initSticky = function () {
    var self = this;
    if (!this.sticky || this.dataset.sticky !== '1') return;

    var anchor = document.querySelector('[data-s3-pro-sentinel]');
    if (!anchor) {
      var triggers = document.querySelectorAll('[data-s3-open-pro-capture]');
      for (var i = 0; i < triggers.length; i++) {
        if (!this.contains(triggers[i])) { anchor = triggers[i]; break; }
      }
    }

    if (anchor && 'IntersectionObserver' in window) {
      this._io = new IntersectionObserver(function (entries) {
        var e = entries[0];
        // Visible, or still below the fold → hide. Scrolled past above → show.
        var scrolledPast = !e.isIntersecting && e.boundingClientRect.top < 0;
        self.setSticky(scrolledPast);
      }, { threshold: 0 });
      this._io.observe(anchor);
      return;
    }

    this._onScroll = function () {
      self.setSticky(window.pageYOffset > window.innerHeight * 0.6);
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this._onScroll();
  };

  proto.setSticky = function (show) {
    if (!this.sticky) return;
    if (show && this.modal && !this.modal.hidden) show = false;
    this._stickyWanted = show;
    this.sticky.hidden = !show;
    if (show) {
      // Next frame so the transition runs from the hidden state.
      var el = this.sticky;
      requestAnimationFrame(function () { el.classList.add('is-in'); });
    } else {
      this.sticky.classList.remove('is-in');
    }
  };

  proto.openModalPublic = function () { this.openModal(); };

  proto.openModal = function () {
    if (!this.modal) return;
    this._lastFocus = document.activeElement;
    this.modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._onKey);
    if (this.sticky) this.sticky.hidden = true;
    var f = this.modal.querySelector('input, button');
    if (f) { try { f.focus(); } catch (e) {} }
    this._dispatch('s3:pro_modal_open');
  };

  proto.closeModal = function (method) {
    if (!this.modal) return;
    var wasOpen = !this.modal.hidden;
    this.modal.hidden = true;
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._onKey);
    if (this.sticky && this._stickyWanted) this.sticky.hidden = false;
    if (this._lastFocus) { try { this._lastFocus.focus(); } catch (e) {} }
    if (wasOpen && !this._captureFired) {
      this._dispatch('s3:pro_modal_dismiss', { method: method || 'unknown' });
    }
  };

  proto.trapTab = function (e) {
    var nodes = this.modal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
    var f = Array.prototype.filter.call(nodes, function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  proto.collectParams = function () {
    var params = new URLSearchParams(location.search), props = {}, i, k, v;
    for (i = 0; i < UTM_KEYS.length; i++) { k = UTM_KEYS[i]; v = params.get(k); if (v) props[labelFor(k)] = v; }
    for (i = 0; i < this.extraParams.length; i++) {
      k = this.extraParams[i];
      if (!k || UTM_KEYS.indexOf(k) !== -1) continue;
      v = params.get(k); if (v) props[labelFor(k)] = v;
    }
    props['Lead Source'] = this.customSource;
    props['Landing Page URL'] = location.href;
    if (document.referrer) props['Referrer'] = document.referrer;
    return props;
  };

  proto.val = function (sel) {
    var el = this.form.querySelector(sel);
    return el ? el.value.trim() : '';
  };

  proto.showError = function (msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  };
  proto.hideError = function () { if (this.errorEl) this.errorEl.hidden = true; };

  proto.setBusy = function (busy) {
    if (!this.submitBtn) return;
    if (busy) { this.submitBtn.setAttribute('aria-busy', 'true'); this.submitBtn.disabled = true; }
    else { this.submitBtn.removeAttribute('aria-busy'); this.submitBtn.disabled = false; }
  };

  proto.onSubmit = function (e) {
    e.preventDefault();
    var self = this;

    // Honeypot: a real person never fills this. Show the success state so a
    // bot learns nothing, and send nothing to Klaviyo.
    if (this.val('[data-capture-hp]')) { this.showSuccess(); return; }

    var data = {
      first: this.val('[data-capture-first]'),
      last: this.val('[data-capture-last]'),
      email: this.val('[data-capture-email]'),
      gdc: this.val('[data-capture-gdc]'),
      practice: this.val('[data-capture-practice]'),
      addr1: this.val('[data-capture-addr1]'),
      addr2: this.val('[data-capture-addr2]'),
      city: this.val('[data-capture-city]'),
      postcode: this.val('[data-capture-postcode]')
    };

    // Opt-in: absent checkbox (switched off in the editor) counts as no opt-in.
    var consentEl = this.form.querySelector('[data-capture-consent]');
    data.optIn = !!(consentEl && consentEl.checked);
    data.optInWording = consentEl ? (consentEl.parentNode.textContent || '').trim() : '';

    this.hideError();

    var required = [
      ['first', 'Please enter your first name.', '[data-capture-first]'],
      ['last', 'Please enter your surname.', '[data-capture-last]'],
      ['email', 'Please enter a valid email address.', '[data-capture-email]'],
      ['gdc', 'Please enter your GDC number.', '[data-capture-gdc]'],
      ['practice', 'Please enter your practice name.', '[data-capture-practice]'],
      ['addr1', 'Please enter the practice address.', '[data-capture-addr1]'],
      ['city', 'Please enter the town or city.', '[data-capture-city]'],
      ['postcode', 'Please enter the practice postcode.', '[data-capture-postcode]']
    ];

    for (var i = 0; i < required.length; i++) {
      var key = required[i][0];
      var el = this.form.querySelector(required[i][2]);
      if (!el) continue;                       // field switched off in the editor
      if (!data[key] || (key === 'email' && !validEmail(data.email))) {
        this.showError(required[i][1]);
        try { el.focus(); } catch (err) {}
        return;
      }
    }

    this.setBusy(true);
    var props = this.collectParams();
    // Practice name and the address map to STANDARD Klaviyo profile fields in
    // submitToKlaviyo (organization + location), not to properties, so they are
    // segmentable and export cleanly for fulfilment. Only what has no standard
    // home is written as a property.
    props['GDC Number'] = data.gdc;
    props['Professional Type'] = 'Dental professional';

    // Opt-in audit trail. Klaviyo's own consent state is set by the endpoint we
    // choose below; these record HOW it was obtained, which is what makes the
    // legitimate-interests basis defensible if it is ever questioned.
    props['Marketing Opt-In'] = data.optIn;
    props['Opt-In Method'] = data.optIn ? 'Pre-ticked box, left ticked' : 'Pre-ticked box, unticked by user';
    props['Opt-In Source'] = 'Professionals page sample request';
    props['Opt-In Timestamp'] = new Date().toISOString();
    if (data.optInWording) props['Opt-In Wording'] = data.optInWording;

    this.submitToKlaviyo(data, props)
      .then(function (responses) {
        var ok = Array.isArray(responses) && responses.every(function (r) { return r && r.ok; });
        if (ok) {
          self._captureFired = true;
          self._dispatch('s3:pro_sample_request', { email: data.email, gdc: data.gdc });
        }
      })
      .catch(function (err) { console.warn('S3 professional capture: Klaviyo submit failed', err); })
      .then(function () { self.showSuccess(); self.setBusy(false); });
  };

  proto.submitToKlaviyo = function (data, props) {
    var companyId = this.companyId, listId = this.listId;
    if (!companyId) return Promise.resolve();

    var headers = { 'Content-Type': 'application/vnd.api+json', revision: '2026-04-15' };

    // Standard Klaviyo profile fields wherever one exists, so the data is
    // segmentable and exports cleanly. Location values are free-text strings and
    // are not validated the way phone_number is, so mapping them is safe — that
    // earlier lesson applies to phone only, which is why we still do not send it.
    var attrs = { email: data.email, properties: props };
    if (data.first) attrs.first_name = data.first;
    if (data.last) attrs.last_name = data.last;
    if (data.practice) attrs.organization = data.practice;

    var loc = {};
    if (data.addr1) loc.address1 = data.addr1;
    if (data.addr2) loc.address2 = data.addr2;
    if (data.city) loc.city = data.city;
    if (data.postcode) loc.zip = data.postcode;
    if (data.addr1 || data.city || data.postcode) loc.country = 'United Kingdom';
    if (Object.keys(loc).length) attrs.location = loc;

    var calls = [];

    if (data.optIn && listId) {
      // Opted in: subscribe to the list. This endpoint sets Klaviyo's own email
      // marketing consent, so it is only ever called when the box is ticked.
      calls.push(fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: this.customSource,
              profile: { data: { type: 'profile', attributes: attrs } }
            },
            relationships: { list: { data: { type: 'list', id: listId } } }
          }
        })
      }));
    } else {
      // Not opted in: write the profile WITHOUT setting marketing consent, so the
      // sample request is still fulfilled and the practice is still on record.
      calls.push(fetch('https://a.klaviyo.com/client/profiles?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ data: { type: 'profile', attributes: attrs } })
      }));
    }

    // The event fires either way: it is what a fulfilment flow triggers on, so a
    // request is never lost because someone declined marketing.
    calls.push(fetch('https://a.klaviyo.com/client/events?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties: props,
            metric: { data: { type: 'metric', attributes: { name: 'Requested Professional Samples' } } },
            profile: { data: { type: 'profile', attributes: attrs } }
          }
        }
      })
    }));

    return Promise.all(calls);
  };

  proto.showSuccess = function () {
    if (this.formState) this.formState.hidden = true;
    if (this.successEl) this.successEl.hidden = false;
    this.setBusy(false);
  };

  proto._dispatch = function (name, detail) {
    try { document.dispatchEvent(new CustomEvent(name, { detail: detail || {}, bubbles: true })); } catch (e) {}
  };

  customElements.define('s3-pro-capture', S3ProCapture);

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest ? e.target.closest('[data-s3-open-pro-capture]') : null;
    if (!trigger) return;
    var host = document.querySelector('s3-pro-capture');
    if (host && host.openModalPublic) { e.preventDefault(); host.openModalPublic(); }
  });
})();
